import {
  Box,
  ClickAwayListener,
  Popper,
  SelectChangeEvent,
} from '@mui/material';
import { FC } from 'react';

import { DateRangePicker, getStripTime, TimePicker } from '@abg/kit-base';

import { useDateTimePickerController } from './hooks/use-date-time-picker-controller';
import { DateTimePickerErrorBanner } from './molecules/DateTimePickerErrorBanner';
import { DateTimePickerFooter } from './molecules/DateTimePickerFooter';
import {
  dateTimePopperContainerStyles,
  dateTimePopperStyles,
  timePickerContainerStyles,
  timePickerWrapperStyles,
} from './DateTimePickerDesktop.styling';
import {
  DateTimePickerErrorEnum,
  DateTimePickerProperties,
  InitialDraftStateT,
} from './types';

export const DateTimePickerDesktop: FC<DateTimePickerProperties> = ({
  isOpen,
  anchorRef,
  onClose,
  onDone,
  uiTextData,
  timePickerOptions,
  pickUpOpeningHours = [],
  pickupHolidays = [],
  dropoffOpeningHours = [],
  dropoffHolidays = [],
  dropoff24hrsFlag,
  dateRangePickerProps,
  initialDraftState,
  maxBookingPeriod,
  brand,
  weekStartsOn,
  locale,
  timeFormat,
  disabledRange,
}) => {
  const {
    draftValues,
    nonWorkingPickupHolidays,
    nonWorkingDropoffHolidays,
    todayAsUnavailableDay,
    pickupSubtitle,
    dropoffSubtitle,
    pickupLocationTimeOptions,
    dropoffLocationTimeOptions,
    handleValueChange,
    handleCancel,
    handleDone,
    pickUpLocationClosedDays,
    dropoffLocationClosedDays,
    daysSelected,
    showDateTimePickerError,
  } = useDateTimePickerController({
    onClose,
    onDone,
    uiTextData,
    timePickerOptions,
    pickUpOpeningHours,
    pickupHolidays,
    dropoffOpeningHours,
    dropoffHolidays,
    dropoff24hrsFlag,
    initialDraftState,
    timeFormat,
    maxBookingPeriod,
  });

  const { timePickerPickupTitle, timePickerDropoffTitle } = uiTextData;
  const isDropoffTimeUnreachable = dropoffLocationTimeOptions.length === 0;

  return (
    <Popper
      open={isOpen}
      anchorEl={anchorRef.current}
      placement="bottom-end"
      disablePortal
      sx={dateTimePopperStyles}
    >
      <ClickAwayListener onClickAway={handleCancel}>
        <Box
          sx={dateTimePopperContainerStyles(brand)}
          data-testid="dateTimePicker"
        >
          <DateRangePicker
            value={draftValues.dates}
            weekStartsOn={weekStartsOn}
            locale={locale}
            minDateValue={dateRangePickerProps.dateRangePickerMinDateValue}
            maxDateValue={dateRangePickerProps.dateRangePickerMaxDateValue}
            disabledRange={disabledRange}
            maxRentalDays={
              draftValues.dates?.from &&
              draftValues.dates?.to &&
              getStripTime(draftValues.dates.from) !==
                getStripTime(draftValues.dates.to)
                ? undefined
                : maxBookingPeriod
            }
            maxRentalStartDate={
              draftValues.dates?.from &&
              draftValues.dates?.to &&
              getStripTime(draftValues.dates.from) !==
                getStripTime(draftValues.dates.to)
                ? undefined
                : draftValues.dates?.from
            }
            onChange={(dates: InitialDraftStateT['dates']) =>
              handleValueChange('dates', dates)
            }
            pickUpLocationHolidays={[
              todayAsUnavailableDay,
              ...nonWorkingPickupHolidays,
            ]}
            pickUpLocationClosedDays={pickUpLocationClosedDays}
            dropOffLocationHolidays={
              dropoff24hrsFlag ? [] : nonWorkingDropoffHolidays
            }
            dropOffLocationClosedDays={
              dropoff24hrsFlag ? [] : dropoffLocationClosedDays
            }
          />

          {showDateTimePickerError && (
            <DateTimePickerErrorBanner
              showDateTimePickerError={
                showDateTimePickerError as DateTimePickerErrorEnum
              }
              maxBookingPeriod={maxBookingPeriod}
            />
          )}

          <Box sx={timePickerContainerStyles}>
            <Box sx={timePickerWrapperStyles}>
              <TimePicker
                value={draftValues.pickupTime}
                title={timePickerPickupTitle}
                subtitle={pickupSubtitle}
                onChange={(event_: SelectChangeEvent<string>) =>
                  handleValueChange('pickupTime', event_.target.value)
                }
                timeOptions={pickupLocationTimeOptions}
              />
            </Box>

            <TimePicker
              value={draftValues.dropoffTime}
              title={timePickerDropoffTitle}
              subtitle={dropoffSubtitle}
              onChange={(event_: SelectChangeEvent<string>) =>
                handleValueChange('dropoffTime', event_.target.value)
              }
              timeOptions={dropoffLocationTimeOptions}
            />
          </Box>

          <DateTimePickerFooter
            brand={brand}
            isDropoffTimeUnreachable={isDropoffTimeUnreachable}
            uiTextData={uiTextData}
            daysSelected={daysSelected}
            showDateTimePickerError={showDateTimePickerError}
            onClose={handleCancel}
            onDone={handleDone}
          />
        </Box>
      </ClickAwayListener>
    </Popper>
  );
};
